<script>
import axios from "axios";
import _ from "lodash";
import Pagination from "@/router/layouts/table-pagination.vue";
import DetailMasterBlueprint from "./detail-master-blueprint.vue";
import { logActivityPeserta, getPesertaInfo } from "@/helpers/logService";

export default {
  name: "TabMasterBlueprint",
  components: {
    Pagination,
    DetailMasterBlueprint,
  },
  data() {
    return {
      currentView: "list", // "list" | "detail"
      selectedIdKompetensi: null,

      loadingTable: false,
      tableData: [],

      // Filter state
      tableFilter: {
        page: 1,
        per_page: 10,
        keyword: "",
        search: "",
        dahan_profesi: "",
        profesi: "",
        kode_kompetensi: "",
        kompetensi: "",
        level: "",
        status: 1,
        column: "nama_kompetensi",
        dir: "asc",
      },

      // Dropdown options
      optionDahanProfesi: [],
      optionProfesi: [],
      optionFilterKodeKompetensi: [],
      optionKompetensi: [],
      optionLevel: [],

      pagination: {
        total: 0,
        from: 0,
        to: 0,
        page: 1,
        per_page: 10,
        links: [],
      },
    };
  },
  created() {
    this.debouncedApplyFilter = _.debounce(() => {
      this.applyFilter();
    }, 500);

    this.debouncedGetDahanProfesi = _.debounce((search, loading) => {
      this.getDahanProfesi(search, loading);
    }, 400);

    this.debouncedGetProfesi = _.debounce((search, loading) => {
      this.getProfesi(search, loading);
    }, 400);

    this.debouncedGetKodeKompetensiFilter = _.debounce((search, loading) => {
      this.getKodeKompetensiFilter(search, loading);
    }, 400);

    this.debouncedGetKompetensiFilter = _.debounce((search, loading) => {
      this.getKompetensiFilter(search, loading);
    }, 400);
  },
  async mounted() {
    await Promise.all([
      this.getDahanProfesi(),
      this.getProfesi(),
      this.getKompetensiFilter(),
      this.getKodeKompetensiFilter(),
      this.getLevelKompetensi(),
    ]);

    await this.getDataTable();
  },
  methods: {
    getAuthToken() {
      return (
        localStorage.access_token ||
        localStorage.access_token_peserta ||
        localStorage.session ||
        ""
      );
    },

    // ================= DATA TABLE =================
    async getDataTable(resetPage = true) {
      if (resetPage) {
        this.tableFilter.page = 1;
      }
      this.loadingTable = true;
      try {
        const token = this.getAuthToken();
        const activeSearch = this.tableFilter.keyword || this.tableFilter.search || "";
        const params = {
          ...this.tableFilter,
          search: activeSearch,
          keyword: activeSearch,
        };
        const response = await axios.get(
          `${process.env.VUE_APP_BACKEND_URL_VERSION}master/kompetensi`,
          {
            params,
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const resData = response.data?.data;
        if (resData) {
          this.tableData = resData.data || [];
          this.pagination.total = resData.total || 0;
          this.pagination.from = resData.from || 0;
          this.pagination.to = resData.to || 0;
          this.pagination.links = resData.links || [];
          this.pagination.page = resData.current_page || this.tableFilter.page;
          this.pagination.per_page = resData.per_page || this.tableFilter.per_page;
        }
      } catch (error) {
        console.error("Gagal mengambil data master kompetensi:", error);
      } finally {
        this.loadingTable = false;
      }
    },

    toPage(url) {
      if (!url) return;
      const link = new URLSearchParams(url.split("?")[1] || "");
      const targetPage = link.get("page");
      if (targetPage) {
        this.tableFilter.page = parseInt(targetPage, 10);
        this.getDataTable(false);
      }
    },

    resetFilter() {
      this.tableFilter.keyword = "";
      this.tableFilter.search = "";
      this.tableFilter.dahan_profesi = "";
      this.tableFilter.profesi = "";
      this.tableFilter.kode_kompetensi = "";
      this.tableFilter.kompetensi = "";
      this.tableFilter.level = "";
      this.getDataTable(true);
      this.getProfesi();
      this.getKompetensiFilter();
      this.getKodeKompetensiFilter();
    },

    clearKeyword() {
      this.tableFilter.keyword = "";
      this.tableFilter.search = "";
      this.applyFilter();
    },

    applyFilter() {
      this.getDataTable(true);
    },

    // ================= DROPDOWN SEARCH HANDLERS =================
    filterDahanProfesi(search, loading) {
      if (loading) loading(true);
      this.debouncedGetDahanProfesi(search, loading);
    },
    async getDahanProfesi(search = "", loading = null) {
      try {
        const token = this.getAuthToken();
        const res = await axios.get(
          `${process.env.VUE_APP_BACKEND_URL_VERSION}referensi/dahan-profesi/all`,
          {
            params: { search, limit: 100 },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        this.optionDahanProfesi = res.data?.data || [];
      } catch (e) {
        console.error(e);
      } finally {
        if (loading) loading(false);
      }
    },

    filterProfesi(search, loading) {
      if (loading) loading(true);
      this.debouncedGetProfesi(search, loading);
    },
    async getProfesi(search = "", loading = null) {
      try {
        const token = this.getAuthToken();
        const params = { search, limit: 100 };
        if (this.tableFilter.dahan_profesi) {
          params.iddahanprofesi = this.tableFilter.dahan_profesi;
        }
        const res = await axios.get(
          `${process.env.VUE_APP_BACKEND_URL_VERSION}referensi/profesi/all`,
          {
            params,
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        this.optionProfesi = res.data?.data || [];
      } catch (e) {
        console.error(e);
      } finally {
        if (loading) loading(false);
      }
    },

    filterKodeKompetensi(search, loading) {
      if (loading) loading(true);
      this.debouncedGetKodeKompetensiFilter(search, loading);
    },
    async getKodeKompetensiFilter(search = "", loading = null) {
      try {
        const token = this.getAuthToken();
        const res = await axios.get(
          `${process.env.VUE_APP_BACKEND_URL_VERSION}referensi/kompetensi/all`,
          {
            params: { search, limit: 100, col: "kode_kompetensi" },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        this.optionFilterKodeKompetensi = res.data?.data || [];
      } catch (e) {
        console.error(e);
      } finally {
        if (loading) loading(false);
      }
    },

    filterKompetensi(search, loading) {
      if (loading) loading(true);
      this.debouncedGetKompetensiFilter(search, loading);
    },
    async getKompetensiFilter(search = "", loading = null) {
      try {
        const token = this.getAuthToken();
        const res = await axios.get(
          `${process.env.VUE_APP_BACKEND_URL_VERSION}referensi/kompetensi/all`,
          {
            params: { search, limit: 100, col: "nama_kompetensi" },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        this.optionKompetensi = res.data?.data || [];
      } catch (e) {
        console.error(e);
      } finally {
        if (loading) loading(false);
      }
    },

    async getLevelKompetensi() {
      try {
        const token = this.getAuthToken();
        const res = await axios.get(
          `${process.env.VUE_APP_BACKEND_URL_VERSION}master/level-kompetensi`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        this.optionLevel = res.data?.data || [];
      } catch (e) {
        console.error(e);
      }
    },

    // ================= ACTIONS =================
    openDetail(row) {
      this.selectedIdKompetensi = row.idkompetensi;
      this.currentView = "detail";
    },

    backToList() {
      this.currentView = "list";
      this.selectedIdKompetensi = null;
    },

    async handleExportPdf(row) {
      if (!row || !row.idkompetensi) return;
      const peserta = getPesertaInfo();
      const params = new URLSearchParams();
      if (peserta.nip) params.append("nip", peserta.nip);
      if (peserta.nama) params.append("nama", peserta.nama);
      const queryString = params.toString() ? `?${params.toString()}` : "";

      const url = `${process.env.VUE_APP_BACKEND_URL_VERSION}export-pdf/blueprint-peserta/${row.idkompetensi}${queryString}`;
      
      // Log aktivitas peserta: Download blueprint
      await logActivityPeserta("Master Blueprint", "Download blueprint", url);

      window.open(url, "_blank");
    },

    formatLevelBadge(row) {
      if (!row) return "-";
      if (row.nama_level) return row.nama_level;
      if (row.idlevel_kompetensi) return `Level ${row.idlevel_kompetensi}`;
      return "-";
    },
  },
};
</script>

<template>
  <div class="master-blueprint-container">
    <!-- JIKA SEDANG DI DETAIL VIEW -->
    <DetailMasterBlueprint
      v-if="currentView === 'detail' && selectedIdKompetensi"
      :idkompetensi="selectedIdKompetensi"
      @back="backToList"
    />

    <!-- JIKA SEDANG DI LIST VIEW -->
    <div v-else>
      <!-- BANNER HEADER -->
      <div class="blueprint-banner d-flex align-items-center mb-4">
        <div class="banner-icon-box me-3">
          <i class="fas fa-file-alt text-primary fa-lg"></i>
        </div>
        <div>
          <h4 class="mb-0 text-white fw-bold">Master Blueprint</h4>
        </div>
      </div>

      <!-- FILTER DATA CARD -->
      <div class="card blueprint-card mb-4">
        <div class="card-header bg-transparent border-0 pt-4 pb-2 px-4">
          <div class="d-flex align-items-center">
            <i class="fas fa-filter text-primary me-2 font-size-16"></i>
            <h5 class="mb-0 fw-bold text-dark font-size-16">Filter Data</h5>
          </div>
        </div>
        <div class="card-body px-4 pb-4 pt-2">
          <div class="row g-3">
            <!-- Keyword (Semua Kolom) -->
            <div class="col-lg-4 col-md-6">
              <label class="form-label font-size-13 fw-semibold text-muted mb-1">
                Keyword (Semua Kolom)
              </label>
              <div class="input-group search-input-group">
                <span class="input-group-text bg-white border-end-0 text-muted">
                  <i class="fas fa-search"></i>
                </span>
                <input
                  type="text"
                  class="form-control border-start-0 ps-1 font-size-13 filter-input-keyword"
                  v-model="tableFilter.keyword"
                  placeholder="Ketik keyword untuk semua kolom..."
                  @input="debouncedApplyFilter"
                  @keyup.enter="applyFilter"
                />
                <button
                  v-if="tableFilter.keyword"
                  class="btn btn-outline-secondary border-start-0 border-end-1"
                  type="button"
                  @click="clearKeyword"
                  title="Hapus keyword"
                >
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </div>

            <!-- Dahan Profesi -->
            <div class="col-lg-4 col-md-6">
              <label class="form-label font-size-13 fw-semibold text-muted mb-1">
                Dahan Profesi
              </label>
              <v-select
                class="style-chooser"
                :options="optionDahanProfesi"
                :reduce="(item) => item.dp_id || item.id_dahan_profesi"
                label="dp_name"
                v-model="tableFilter.dahan_profesi"
                placeholder="Pilih Dahan Profesi"
                :filterable="false"
                @search="filterDahanProfesi"
                @update:modelValue="applyFilter"
              >
                <template #no-options="{ search }">
                  <div class="text-muted p-2 text-center font-size-12">
                    <span v-if="search">Tidak ada data untuk "{{ search }}"</span>
                    <span v-else>Ketik untuk mencari...</span>
                  </div>
                </template>
              </v-select>
            </div>

            <!-- Nama Profesi -->
            <div class="col-lg-4 col-md-6">
              <label class="form-label font-size-13 fw-semibold text-muted mb-1">
                Nama Profesi
              </label>
              <v-select
                class="style-chooser"
                :options="optionProfesi"
                :reduce="(item) => item.id_mprofesi || item.idprofesi"
                label="nama_profesi"
                v-model="tableFilter.profesi"
                placeholder="Pilih Nama Profesi"
                :filterable="false"
                @search="filterProfesi"
                @update:modelValue="applyFilter"
              >
                <template #no-options="{ search }">
                  <div class="text-muted p-2 text-center font-size-12">
                    <span v-if="search">Tidak ada data untuk "{{ search }}"</span>
                    <span v-else>Ketik untuk mencari...</span>
                  </div>
                </template>
              </v-select>
            </div>

            <!-- Kode Kompetensi -->
            <div class="col-lg-4 col-md-6">
              <label class="form-label font-size-13 fw-semibold text-muted mb-1">
                Kode Kompetensi
              </label>
              <v-select
                class="style-chooser"
                :options="optionFilterKodeKompetensi"
                :reduce="(item) => item.kode_kompetensi"
                label="kode_kompetensi"
                v-model="tableFilter.kode_kompetensi"
                placeholder="Pilih Kode Kompetensi"
                :filterable="false"
                @search="filterKodeKompetensi"
                @update:modelValue="applyFilter"
              >
                <template #no-options="{ search }">
                  <div class="text-muted p-2 text-center font-size-12">
                    <span v-if="search">Tidak ada data untuk "{{ search }}"</span>
                    <span v-else>Ketik untuk mencari...</span>
                  </div>
                </template>
              </v-select>
            </div>

            <!-- Nama Kompetensi -->
            <div class="col-lg-4 col-md-6">
              <label class="form-label font-size-13 fw-semibold text-muted mb-1">
                Nama Kompetensi
              </label>
              <v-select
                class="style-chooser"
                :options="optionKompetensi"
                :reduce="(item) => item.nama_kompetensi"
                label="nama_kompetensi"
                v-model="tableFilter.kompetensi"
                placeholder="Pilih Nama Kompetensi"
                :filterable="false"
                @search="filterKompetensi"
                @update:modelValue="applyFilter"
              >
                <template #no-options="{ search }">
                  <div class="text-muted p-2 text-center font-size-12">
                    <span v-if="search">Tidak ada data untuk "{{ search }}"</span>
                    <span v-else>Ketik untuk mencari...</span>
                  </div>
                </template>
              </v-select>
            </div>

            <!-- Level Proficiency -->
            <div class="col-lg-4 col-md-6">
              <label class="form-label font-size-13 fw-semibold text-muted mb-1">
                Level Proficiency
              </label>
              <v-select
                class="style-chooser"
                :options="optionLevel"
                :reduce="(item) => item.nama_level || item.idlevel_kompetensi"
                label="nama_level"
                v-model="tableFilter.level"
                placeholder="Pilih Level Proficiency"
                @update:modelValue="applyFilter"
              >
                <template #no-options="{ search }">
                  <div class="text-muted p-2 text-center font-size-12">
                    <span v-if="search">Tidak ada data untuk "{{ search }}"</span>
                    <span v-else>Ketik untuk mencari...</span>
                  </div>
                </template>
              </v-select>
            </div>
          </div>

          <!-- Buttons Filter -->
          <div class="d-flex justify-content-end align-items-center mt-3 pt-2">
            <button
              type="button"
              class="btn btn-outline-secondary px-4 py-2 me-2 btn-filter-reset rounded-3"
              @click="resetFilter"
            >
              <i class="fas fa-redo-alt me-2"></i> Reset
            </button>
            <button
              type="button"
              class="btn btn-primary px-4 py-2 btn-filter-apply rounded-3"
              @click="applyFilter"
            >
              <i class="fas fa-search me-2"></i> Terapkan Filter
            </button>
          </div>
        </div>
      </div>

      <!-- DATA MASTER BLUEPRINT CARD -->
      <div class="card blueprint-card mb-4">
        <div class="card-header bg-transparent border-0 pt-4 pb-3 px-4 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div class="d-flex align-items-center">
            <div class="table-icon-box me-2">
              <i class="fas fa-table text-primary"></i>
            </div>
            <h5 class="mb-0 fw-bold text-dark font-size-16">
              Data Master Blueprint
            </h5>
          </div>
          <div class="text-muted font-size-13">
            Menampilkan {{ pagination.from || 0 }} - {{ pagination.to || 0 }} dari {{ pagination.total || 0 }} data
          </div>
        </div>

        <div class="card-body px-4 pb-4 pt-0">
          <div class="table-responsive rounded-3 border">
            <table class="table table-hover mb-0 blueprint-table">
              <thead>
                <tr>
                  <th class="text-center" style="width: 50px;">No</th>
                  <th style="min-width: 180px;">Dahan Profesi</th>
                  <th style="min-width: 180px;">Nama Profesi</th>
                  <th style="width: 150px;">Kode Kompetensi</th>
                  <th style="min-width: 220px;">Nama Kompetensi</th>
                  <th class="text-center" style="width: 140px;">Level Proficiency</th>
                  <th class="text-center" style="width: 180px;">Aksi</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loadingTable">
                  <td colspan="7" class="text-center py-5">
                    <div class="spinner-border text-primary spinner-border-sm me-2" role="status"></div>
                    <span class="text-muted">Memuat data...</span>
                  </td>
                </tr>

                <tr v-else-if="!tableData || tableData.length === 0">
                  <td colspan="7" class="text-center py-5 text-muted">
                    Data tidak ditemukan.
                  </td>
                </tr>

                <tr v-else v-for="(row, idx) in tableData" :key="row.idkompetensi || idx">
                  <td class="text-center align-middle">
                    {{ (pagination.from || 1) + idx }}
                  </td>
                  <td class="align-middle text-dark">
                    {{ row.nama_dahan || "-" }}
                  </td>
                  <td class="align-middle text-dark">
                    {{ row.nama_profesi || "-" }}
                  </td>
                  <td class="align-middle text-dark fw-medium">
                    {{ row.kode_kompetensi || "-" }}
                  </td>
                  <td class="align-middle text-dark fw-semibold">
                    {{ row.nama_kompetensi || "-" }}
                  </td>
                  <td class="text-center align-middle">
                    <span class="badge bg-light-primary text-primary px-3 py-2 rounded-pill font-size-12 fw-semibold">
                      {{ formatLevelBadge(row) }}
                    </span>
                  </td>
                  <td class="text-center align-middle">
                    <div class="d-inline-flex gap-2">
                      <button
                        type="button"
                        class="btn btn-sm btn-export-pdf text-white px-3 py-1 rounded-3 d-inline-flex align-items-center"
                        @click="handleExportPdf(row)"
                      >
                        <i class="fas fa-file-pdf me-1"></i> Export PDF
                      </button>
                      <button
                        type="button"
                        class="btn btn-sm btn-detail text-white px-3 py-1 rounded-3 d-inline-flex align-items-center"
                        @click="openDetail(row)"
                      >
                        <i class="fas fa-eye me-1"></i> Detail
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div class="mt-3">
            <Pagination :pagination="pagination" @to-page="toPage" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.master-blueprint-container {
  color: #334155;
}

/* Banner */
.blueprint-banner {
  background: linear-gradient(135deg, #1d72ec 0%, #3b82f6 50%, #60a5fa 100%);
  border-radius: 12px;
  padding: 22px 28px;
  box-shadow: 0 8px 20px rgba(29, 114, 236, 0.2);
}

.banner-icon-box {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
}

/* Cards */
.blueprint-card {
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background-color: #ffffff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
}

.table-icon-box {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background-color: #eff6ff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.font-size-16 {
  font-size: 16px;
}

.font-size-13 {
  font-size: 13px;
}

.font-size-12 {
  font-size: 12px;
}

/* Table */
.blueprint-table thead tr th {
  background-color: #f8fafc !important;
  color: #475569 !important;
  font-weight: 600;
  font-size: 13px;
  border-color: #e2e8f0 !important;
  padding: 14px 12px;
  vertical-align: middle;
}

.blueprint-table tbody td {
  padding: 13px 12px;
  border-color: #f1f5f9;
  font-size: 13px;
}

.bg-light-primary {
  background-color: #eff6ff !important;
  color: #1d72ec !important;
  border: 1px solid #bfdbfe;
}

/* Action Buttons */
.btn-export-pdf {
  background-color: #ef4444;
  border: none;
  font-weight: 500;
  font-size: 12px;
  transition: all 0.2s ease;
}

.btn-export-pdf:hover {
  background-color: #dc2626;
  transform: translateY(-1px);
}

.btn-detail {
  background-color: #1d72ec;
  border: none;
  font-weight: 500;
  font-size: 12px;
  transition: all 0.2s ease;
}

.btn-detail:hover {
  background-color: #1a5ec4;
  transform: translateY(-1px);
}

.btn-filter-apply {
  background-color: #1d72ec;
  border-color: #1d72ec;
  font-weight: 600;
}

.btn-filter-apply:hover {
  background-color: #1a5ec4;
  border-color: #1a5ec4;
}

.btn-filter-reset {
  font-weight: 600;
}

/* Dropdown v-select styling */
.style-chooser .vs__dropdown-toggle {
  border: 1px solid #ced4da;
  border-radius: 8px;
  background-color: #ffffff;
  min-height: 38px;
  padding: 2px 4px;
}

.style-chooser .vs__selected {
  font-size: 13px;
  color: #334155;
}

.style-chooser .vs__search {
  font-size: 13px;
  color: #334155;
}

.style-chooser .vs__search::placeholder {
  color: #94a3b8;
  font-size: 13px;
}

.style-chooser .vs__dropdown-menu {
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
  font-size: 13px;
}

.style-chooser .vs__dropdown-option--highlight {
  background: #1d72ec;
  color: #ffffff;
}

/* Keyword input styling */
.search-input-group {
  border-radius: 8px;
}

.search-input-group .input-group-text {
  border-color: #ced4da;
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
  height: 38px;
  color: #94a3b8;
  font-size: 13px;
}

.filter-input-keyword {
  border-color: #ced4da;
  height: 38px;
  font-size: 13px;
  color: #334155;
}

.filter-input-keyword:focus {
  border-color: #1d72ec;
  box-shadow: none;
}

.search-input-group .btn-outline-secondary {
  border-color: #ced4da;
  color: #64748b;
  height: 38px;
  font-size: 12px;
}

.search-input-group .btn-outline-secondary:hover {
  background-color: #f1f5f9;
  color: #0f172a;
}
</style>
